import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './PatientInfoPanel.css';
import './PatientVisitInformation.css';
import './AddPackage.css';
import { dateTimeFormatter, dateTimeFormatter3 } from '../../../CommonComponents/Utils/DateTimeFormatter';
import { API_BASE_URL } from '../../../Constants/ApiEndpoints';
import axios from 'axios';
import { FaTimesCircle, FaExclamationTriangle, FaTimes, FaCalendarPlus, FaPrint, FaCheck } from 'react-icons/fa';
import { MdContentCopy } from 'react-icons/md';
import { toast } from 'react-toastify';
import PrintAppointment from '../../../CommonComponents/Invoice Receipts/PrintAppointment';

const formatDateStr = (dateStr) => {
  if (!dateStr) return 'N/A';
  try {
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return dateStr;
    return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch (e) {
    return dateStr;
  }
};

const PatientVisitInformation = ({ patient, onAppointmentCancelled }) => {
  const navigate = useNavigate();
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showPrintApptModal, setShowPrintApptModal] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [copied, setCopied] = useState(false);

  const handleCopyApptId = () => {
    if (!patient.appointmentId) return;
    navigator.clipboard.writeText(patient.appointmentId);
    setCopied(true);
    toast.success('Appointment number copied to clipboard!');
    setTimeout(() => setCopied(false), 2000);
  };

  const handleCancelAppointment = async () => {
    setCancelling(true);
    try {
      const response = await axios.patch(
        `${API_BASE_URL}/cancelAppointment/${patient.appointmentId}`
      );
      console.log('✅ Appointment cancelled:', response.data);
      toast.success('Appointment cancelled successfully!');
      setShowCancelModal(false);
      if (onAppointmentCancelled) onAppointmentCancelled();
    } catch (error) {
      console.error('❌ Error cancelling appointment:', error);
      toast.error('Failed to cancel appointment.');
    } finally {
      setCancelling(false);
    }
  };

  return (
    <div className="panel-details-grid">
      <div className="detail-row">
        <span className="detail-label">Booked Date:</span>
        <span className="detail-value">{formatDateStr(patient.appointmentDate || patient.bookingDate || patient.createdDate)}</span>
      </div>

      <div className="detail-row">
        <span className="detail-label">Booked Type:</span>
        <span className="detail-value">{patient.bookingType}</span>
      </div>

      <div className="detail-row">
        <span className="detail-label">Booked Time:</span>
        <span className="detail-value">
          {dateTimeFormatter(patient.appointmentDate || patient.bookingDate, patient.timeSlot) || `${formatDateStr(patient.appointmentDate || patient.bookingDate)} ${dateTimeFormatter3(patient.timeSlot)}`}
        </span>
      </div>

      <div className="detail-row">
        <span className="detail-label">Booked By:</span>
        <span className="detail-value">{patient.bookedBy || 'Reception'}</span>
      </div>

      <div className="detail-row">
        <span className="detail-label">Appointment No:</span>
        <div className="appt-id-copy-box">
          <span className="detail-value font-mono">{patient.appointmentId}</span>
          {/* <button
            className="copy-appt-btn"
            onClick={handleCopyApptId}
            title="Copy Appointment Number"
          >
            {copied ? <FaCheck style={{ color: '#16a34a' }} /> : <MdContentCopy />}
          </button> */}
          <button
            className="icon-action-badge"
            onClick={handleCopyApptId}
            title="Copy Appointment Number"
          >
            <MdContentCopy size={13} />
          </button>
        </div>
      </div>

      <div className="detail-row action-row">
        <button
          className={`cancel-appointment-btn ${patient.appointmentStatus === 'CANCELLED' ? 'disabled' : ''}`}
          onClick={patient.appointmentStatus === 'CANCELLED' ? null : () => setShowCancelModal(true)}
          disabled={patient.appointmentStatus === 'CANCELLED'}
        >
          <FaTimesCircle className="cancel-icon" />
          {patient.appointmentStatus === 'CANCELLED' ? 'Cancelled' : 'Cancel Appointment'}
        </button>

        <button
          className="new-appointment-btn"
          onClick={() => navigate('/outpatient/appointments/newAppointment', { state: { patient } })}
          title="Book a new appointment for this patient"
        >
          <FaCalendarPlus className="new-appt-icon" />
          New Appointment
        </button>

        <button
          className="print-appointment-btn"
          onClick={() => setShowPrintApptModal(true)}
          title="Print official appointment slip with header & QR"
        >
          <FaPrint className="print-appt-icon" />
          Print Appointment
        </button>
      </div>

      {/* Confirm Cancel Modal */}
      {showCancelModal && (
        <div className="cancel-appt-overlay" onClick={() => setShowCancelModal(false)}>
          <div className="cancel-appt-modal" onClick={(e) => e.stopPropagation()}>
            <div className="cancel-appt-header">
              <div className="cancel-title-group">
                <div className="cancel-warning-icon-wrapper">
                  <FaExclamationTriangle size={18} color="#dc2626" />
                </div>
                <h3>Cancel Appointment</h3>
              </div>
              <button className="cancel-modal-close-btn" onClick={() => setShowCancelModal(false)}>
                <FaTimes size={14} />
              </button>
            </div>

            <div className="cancel-appt-body">
              <p className="cancel-confirm-msg">
                Are you sure you want to cancel the appointment for <strong>{patient.patientName}</strong>?
              </p>

              <div className="cancel-patient-summary-box">
                <div className="summary-info-item">
                  <span className="info-label">Patient Name:</span>
                  <span className="info-val">{patient.patientName}</span>
                </div>
                <div className="summary-info-item">
                  <span className="info-label">MR Number:</span>
                  <span className="info-val font-mono">{patient.mrNumber}</span>
                </div>
                {patient.appointmentId && (
                  <div className="summary-info-item">
                    <span className="info-label">Appointment ID:</span>
                    <span className="info-val font-mono">{patient.appointmentId}</span>
                  </div>
                )}
              </div>

              <div className="cancel-warning-notice">
                ⚠️ This action will update the appointment status to <strong>CANCELLED</strong>.
              </div>
            </div>

            <div className="cancel-appt-footer">
              <button
                className="cancel-modal-keep-btn"
                onClick={() => setShowCancelModal(false)}
                disabled={cancelling}
              >
                No, Keep Appointment
              </button>

              <button
                className="cancel-modal-confirm-btn"
                onClick={handleCancelAppointment}
                disabled={cancelling}
              >
                {cancelling ? 'Cancelling...' : 'Yes, Cancel Appointment'}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Print Appointment Slip Modal */}
      <PrintAppointment
        isOpen={showPrintApptModal}
        onClose={() => setShowPrintApptModal(false)}
        patient={patient}
      />
    </div>
  );
};

export default PatientVisitInformation;
